import type { FlightLeg } from "./types";
import { fetchFrequentAirportsFromDb } from "../supabase/airports";
import {
  bumpAirportUse,
  filterAirportOptions,
  frequenciesFromLegs,
  getStoredAirportFrequencies,
  isPlausibleAirportCode,
  mapToSortedOptions,
  mergeFrequencyMaps,
  normIcao,
  type AirportOption,
} from "./airportFrequencyLocal";

const DEBOUNCE_MS = 180;

function hideList(ul: HTMLUListElement): void {
  ul.classList.add("hidden");
  ul.innerHTML = "";
  ul.removeAttribute("aria-activedescendant");
}

function positionList(ul: HTMLUListElement, input: HTMLInputElement): void {
  const r = input.getBoundingClientRect();
  ul.style.position = "fixed";
  ul.style.left = `${r.left}px`;
  ul.style.top = `${r.bottom + 2}px`;
  ul.style.minWidth = `${r.width}px`;
  ul.style.zIndex = "100";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function initAirportsAutocomplete(
  root: HTMLElement,
  ctx: {
    hasSupabase: boolean;
    getLegsSnapshot: () => FlightLeg[];
  }
): { refreshAirports: () => Promise<void> } {
  let cache: AirportOption[] = [];
  let refreshInFlight: Promise<void> | null = null;

  async function rebuildCache(): Promise<void> {
    const maps: Map<string, number>[] = [];

    try {
      maps.push(frequenciesFromLegs(ctx.getLegsSnapshot()));
    } catch {
      /* manual form may throw if incomplete */
    }

    maps.push(getStoredAirportFrequencies());

    if (ctx.hasSupabase) {
      const { data, error } = await fetchFrequentAirportsFromDb(80);
      if (!error && data?.length) {
        const m = new Map<string, number>();
        for (const r of data) {
          const c = normIcao(r.icao);
          if (isPlausibleAirportCode(c)) {
            m.set(c, (m.get(c) ?? 0) + r.leg_count);
          }
        }
        maps.push(m);
      }
    }

    const merged = mergeFrequencyMaps(maps);
    cache = mapToSortedOptions(merged);
  }

  function refreshAirports(): Promise<void> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = rebuildCache().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  const state = {
    /** Browser `setTimeout` id (typed as number for tsc with Node typings in the toolchain). */
    timer: undefined as number | undefined,
    activeInput: null as HTMLInputElement | null,
    activeList: null as HTMLUListElement | null,
    items: [] as AirportOption[],
    activeIndex: -1,
  };

  function closeActive(): void {
    if (state.activeList) hideList(state.activeList);
    state.activeInput = null;
    state.activeList = null;
    state.items = [];
    state.activeIndex = -1;
  }

  function renderList(input: HTMLInputElement, opts: AirportOption[]): void {
    let ul = input.parentElement?.querySelector<HTMLUListElement>(
      ".passengers-ac-list.airports-ac"
    );
    if (!ul) {
      ul = document.createElement("ul");
      ul.className = "passengers-ac-list airports-ac hidden";
      ul.setAttribute("role", "listbox");
      ul.setAttribute("aria-label", "Airport suggestions");
      input.parentElement?.appendChild(ul);
    }
    state.activeInput = input;
    state.activeList = ul;
    state.items = opts;
    state.activeIndex = -1;
    ul.innerHTML = "";
    if (opts.length === 0) {
      hideList(ul);
      return;
    }
    if (!input.dataset.acAirId) {
      input.dataset.acAirId = `apt-${Math.random().toString(36).slice(2, 11)}`;
    }
    const idBase = input.dataset.acAirId;
    opts.forEach((o, i) => {
      const li = document.createElement("li");
      li.id = `airports-ac-${idBase}-${i}`;
      li.className = "passengers-ac-item";
      li.setAttribute("role", "option");
      li.dataset.index = String(i);
      li.innerHTML = `<span class="passengers-ac-name">${escapeHtml(o.icao)}</span> <span class="passengers-ac-meta">· ${o.count}×</span>`;
      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        applyPick(input, o.icao);
      });
      ul!.appendChild(li);
    });
    ul.classList.remove("hidden");
    positionList(ul, input);
  }

  function applyPick(input: HTMLInputElement, icao: string): void {
    input.value = normIcao(icao);
    bumpAirportUse(input.value, 2);
    closeActive();
    input.focus();
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function highlightIndex(ul: HTMLUListElement, idx: number): void {
    const lis = ul.querySelectorAll<HTMLLIElement>(".passengers-ac-item");
    lis.forEach((li, i) => {
      li.classList.toggle("passengers-ac-active", i === idx);
      li.setAttribute("aria-selected", i === idx ? "true" : "false");
    });
    const input = state.activeInput;
    if (input && lis[idx]) {
      input.setAttribute("aria-activedescendant", lis[idx]!.id);
    }
  }

  async function runSearch(input: HTMLInputElement): Promise<void> {
    await refreshAirports();
    const q = input.value;
    const opts = filterAirportOptions(cache, q, 15);
    if (opts.length === 0) {
      const ul = input.parentElement?.querySelector<HTMLUListElement>(
        ".passengers-ac-list.airports-ac"
      );
      if (ul) hideList(ul);
      return;
    }
    renderList(input, opts);
  }

  function isAirportField(el: EventTarget | null): el is HTMLInputElement {
    return (
      el instanceof HTMLInputElement &&
      (el.dataset.f === "from" || el.dataset.f === "to")
    );
  }

  root.addEventListener("focusin", (e) => {
    const t = e.target;
    if (!isAirportField(t)) return;
    void runSearch(t);
  });

  root.addEventListener("input", (e) => {
    const t = e.target;
    if (!isAirportField(t)) return;
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      void runSearch(t);
    }, DEBOUNCE_MS);
  });

  root.addEventListener(
    "keydown",
    (e) => {
      const t = e.target;
      if (!isAirportField(t)) return;
      const ul = t.parentElement?.querySelector<HTMLUListElement>(
        ".passengers-ac-list.airports-ac"
      );
      if (!ul || ul.classList.contains("hidden") || state.items.length === 0) {
        if (e.key === "Escape") closeActive();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        hideList(ul);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        state.activeIndex = Math.min(
          state.items.length - 1,
          state.activeIndex + 1
        );
        highlightIndex(ul, state.activeIndex);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        state.activeIndex = Math.max(0, state.activeIndex - 1);
        highlightIndex(ul, state.activeIndex);
        return;
      }
      if (e.key === "Enter") {
        const idx =
          state.activeIndex >= 0 ? state.activeIndex : state.items.length > 0 ? 0 : -1;
        if (idx >= 0) {
          e.preventDefault();
          const pick = state.items[idx]?.icao;
          if (pick) applyPick(t, pick);
        }
      }
    },
    true
  );

  root.addEventListener(
    "blur",
    (e) => {
      const t = e.target;
      if (!isAirportField(t)) return;
      window.setTimeout(() => {
        const ae = document.activeElement;
        if (ae && t.parentElement?.contains(ae)) return;
        const ul = t.parentElement?.querySelector(".passengers-ac-list.airports-ac");
        if (ul && ae && ul.contains(ae)) return;
        const v = normIcao(t.value);
        if (isPlausibleAirportCode(v)) bumpAirportUse(v, 1);
        closeActive();
      }, 200);
    },
    true
  );

  window.addEventListener("scroll", () => {
    if (state.activeList && state.activeInput) {
      positionList(state.activeList, state.activeInput);
    }
  });
  window.addEventListener("resize", () => {
    if (state.activeList && state.activeInput) {
      positionList(state.activeList, state.activeInput);
    }
  });

  void refreshAirports();

  return { refreshAirports };
}
